import {
  useQuery as useTanstackQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { Holiday } from '@shiftly/shared';
import { DateTime } from 'luxon';
import { supabase } from '../supabase';

const convertHolidayToDbRecord = (holiday: Partial<Holiday>): Record<string, any> => {
  const updated_ata: Record<string, any> = {
    updated_at: DateTime.utc().toISO()
  };

  Object.entries(holiday).forEach(([key, value]) => {
    if (key === 'id' || key === 'created_at' || key === 'updated_at') return;

    if (value instanceof DateTime) {
      updated_ata[key] = value.toISO();
    } else {
      updated_ata[key] = value;
    }
  });

  return updated_ata;
};

const convertDbRecordToHoliday = (record: any): Holiday => {
  return {
    ...record,
    start_date: DateTime.fromISO(record.start_date, { zone: 'UTC' }),
    end_date: DateTime.fromISO(record.end_date, { zone: 'UTC' }),
    approved_at: record.approved_at ? DateTime.fromISO(record.approved_at, { zone: 'UTC' }) : undefined,
    created_at: DateTime.fromISO(record.created_at, { zone: 'UTC' }),
    updated_at: DateTime.fromISO(record.updated_at, { zone: 'UTC' })
  };
};

const SupabaseHolidayService = {
  async getHolidays(
    startDate?: DateTime,
    endDate?: DateTime,
    user_id?: string,
    departments?: ('Sales' | 'Mass DMs' | 'Posts' | 'GG Swap')[]
  ) {
    try {
      let query = supabase
        .from('holidays')
        .select('*')
        .order('start_date', { ascending: true });

      if (startDate && endDate) {
        query = query
          .gte('start_date', startDate.toISO())
          .lte('start_date', endDate.toISO());
      }

      if (user_id) {
        query = query.eq('user_id', user_id);
      }

      if (departments && departments.length > 0) {
        query = query.overlaps('departments', departments);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error getting holidays:', error);
        throw error;
      }

      return data.map(convertDbRecordToHoliday);
    } catch (error) {
      console.error('Error getting holidays:', error);
      throw error;
    }
  },

  async getUserHolidays(user_id: string, startDate?: DateTime, endDate?: DateTime) {
    try {
      let query = supabase
        .from('holidays')
        .select('*')
        .eq('user_id', user_id)
        .order('start_date', { ascending: true });

      if (startDate && endDate) {
        query = query
          .gte('start_date', startDate.toISO())
          .lte('start_date', endDate.toISO());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error getting user holidays:', error);
        throw error;
      }

      return data.map(convertDbRecordToHoliday);
    } catch (error) {
      console.error('Error getting user holidays:', error);
      throw error;
    }
  },

  async createHoliday(holiday: Omit<Holiday, 'id' | 'created_at' | 'updated_at' | 'status'>) {
    try {
      const now = DateTime.utc().toISO();

      const holidayData = {
        user_id: holiday.user_id,
        start_date: holiday.start_date.toISO(),
        end_date: holiday.end_date.toISO(),
        status: 'pending' as const,
        type: holiday.type,
        departments: holiday.departments,
        scheduled_models: holiday.scheduled_models || null,
        notes: holiday.notes || null,
        approved_by: holiday.approved_by || null,
        approved_at: holiday.approved_at?.toISO() || null,
        created_at: now,
        updated_at: now
      };

      const { data, error } = await supabase
        .from('holidays')
        .insert(holidayData)
        .select()
        .single();

      if (error) {
        console.error('Error creating holiday:', error);
        throw error;
      }

      return data.id;
    } catch (error) {
      console.error('Error creating holiday:', error);
      throw error;
    }
  },

  async updateHoliday(id: string, holiday: Partial<Holiday>) {
    try {
      const updated_ata = convertHolidayToDbRecord(holiday);

      const { error } = await supabase
        .from('holidays')
        .update(updated_ata)
        .eq('id', id);

      if (error) {
        console.error('Error updating holiday:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error updating holiday:', error);
      throw error;
    }
  },

  async approveHoliday(id: string, approvedBy: string) {
    try {
      const now = DateTime.utc().toISO();

      const { error } = await supabase
        .from('holidays')
        .update({
          status: 'approved',
          approved_by: approvedBy,
          approved_at: now,
          updated_at: now
        })
        .eq('id', id);

      if (error) {
        console.error('Error approving holiday:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error approving holiday:', error);
      throw error;
    }
  },

  async deleteHoliday(id: string) {
    try {
      const { error } = await supabase
        .from('holidays')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting holiday:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error deleting holiday:', error);
      throw error;
    }
  }
};

export const useHolidays = (
  startDate?: DateTime,
  endDate?: DateTime,
  user_id?: string,
  departments?: ('Sales' | 'Mass DMs' | 'Posts' | 'GG Swap')[],
  options?: { enabled?: boolean }
) => {
  return useTanstackQuery({
    queryKey: ['holidays', { startDate, endDate, user_id, departments }],
    queryFn: () => SupabaseHolidayService.getHolidays(startDate, endDate, user_id, departments),
    ...options,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    }
  });
};

export const useUserHolidays = (user_id: string, startDate?: DateTime, endDate?: DateTime) => {
  return useTanstackQuery({
    queryKey: ['holidays', user_id, { startDate, endDate }],
    queryFn: () => SupabaseHolidayService.getUserHolidays(user_id, startDate, endDate),
    enabled: !!user_id,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    }
  });
};

export const useCreateHoliday = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (holiday: Omit<Holiday, 'id' | 'created_at' | 'updated_at' | 'status'>) => 
      SupabaseHolidayService.createHoliday(holiday),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
    }
  });
};

export const useUpdateHoliday = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, holiday }: { id: string; holiday: Partial<Holiday> }) => 
      SupabaseHolidayService.updateHoliday(id, holiday),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      queryClient.invalidateQueries({ queryKey: ['holidays', id] });
    }
  });
};

export const useApproveHoliday = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, approvedBy }: { id: string; approvedBy: string }) => 
      SupabaseHolidayService.approveHoliday(id, approvedBy),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      queryClient.invalidateQueries({ queryKey: ['holidays', id] });
    }
  });
};

export const useDeleteHoliday = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => SupabaseHolidayService.deleteHoliday(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
    }
  });
};

export default SupabaseHolidayService;